import React, { Suspense, useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Loader from "./components/Loader";
import ErrorBoundary from "./components/ErrorBoundary";
import { Loader2 } from "lucide-react";

// Deferred (non-critical) global components — kept off the first-paint path.
const Toaster = React.lazy(() => import("sonner").then((m) => ({ default: m.Toaster })));
const WhatsAppButton = React.lazy(() => import("./components/WhatsAppButton"));

const useIdleMount = (delay = 300) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const fire = () => { if (!cancelled) setMounted(true); };
    const ric = window.requestIdleCallback;
    const id = ric
      ? ric(fire, { timeout: delay })
      : setTimeout(fire, delay);
    return () => {
      cancelled = true;
      if (ric && window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, [delay]);
  return mounted;
};

const DeferredWhatsAppButton = () => {
  const ready = useIdleMount(300);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <WhatsAppButton />
    </Suspense>
  );
};

const DeferredToaster = () => {
  // Toaster is invisible until a toast fires, so we can afford a longer delay
  // for more TBT relief. AdminDashboard/Contact form toasts fire on user action.
  const ready = useIdleMount(800);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-medium)',
            color: 'var(--text-primary)',
            fontFamily: 'Lato, sans-serif',
          },
        }}
      />
    </Suspense>
  );
};

// Lazy-loaded pages for code splitting
const Home = React.lazy(() => import("./pages/Home"));
const About = React.lazy(() => import("./pages/About"));
const Courses = React.lazy(() => import("./pages/Courses"));
const CourseDetail = React.lazy(() => import("./pages/CourseDetail"));
const Contact = React.lazy(() => import("./pages/Contact"));
const Reviews = React.lazy(() => import("./pages/Reviews"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const Login = React.lazy(() => import("./pages/auth/Login"));
const ForgotPassword = React.lazy(() => import("./pages/auth/ForgotPassword"));
const Dashboard = React.lazy(() => import("./pages/dashboard/Dashboard"));
const CommunityForum = React.lazy(() => import("./pages/community/CommunityForum"));
const CommunityThread = React.lazy(() => import("./pages/community/CommunityThread"));
const AdminLogin = React.lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const TermsAndConditions = React.lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = React.lazy(() => import("./pages/RefundPolicy"));
const ShippingPolicy = React.lazy(() => import("./pages/ShippingPolicy"));
const BlogListing = React.lazy(() => import("./pages/BlogListing"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));
const CityCoursePage = React.lazy(() => import("./pages/CityCoursePage"));
const AnswersHub = React.lazy(() => import("./pages/AnswersHub"));
const AnswerPage = React.lazy(() => import("./pages/AnswerPage"));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Route guard for authenticated users
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 className="animate-spin" style={{ color: 'var(--accent)', width: 32, height: 32 }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={requiredRole === 'admin' ? '/admin/login' : '/login'} replace />;
  }

  if (requiredRole === 'admin' && user?.role !== 'admin') {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Suspense fallback for lazy-loaded pages
const PageFallback = () => (
  <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Loader2 className="animate-spin" style={{ color: 'var(--accent)', width: 28, height: 28, opacity: 0.7 }} />
  </div>
);

// Layout with Navbar and Footer for public pages
const PublicLayout = ({ children }) => (
  <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
    <Navbar />
    <main style={{ flex: 1 }}>{children}</main>
    <Footer />
    <DeferredWhatsAppButton />
  </div>
);

// Layout with Navbar only for dashboard/auth pages
const DashboardLayout = ({ children }) => (
  <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
    <Navbar />
    <main style={{ flex: 1 }}>{children}</main>
    <DeferredWhatsAppButton />
  </div>
);

// Minimal layout for auth pages
const AuthLayout = ({ children }) => (
  <div style={{ minHeight: '100vh' }}>
    {children}
  </div>
);

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <DeferredToaster />
            <Loader />
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<PageFallback />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
                <Route path="/courses" element={<PublicLayout><Courses /></PublicLayout>} />
                <Route path="/courses/in/:city" element={<PublicLayout><CityCoursePage /></PublicLayout>} />
                <Route path="/courses/:slug" element={<PublicLayout><CourseDetail /></PublicLayout>} />
                <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
                <Route path="/reviews" element={<PublicLayout><Reviews /></PublicLayout>} />
                <Route path="/blog" element={<PublicLayout><BlogListing /></PublicLayout>} />
                <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
                <Route path="/answers" element={<PublicLayout><AnswersHub /></PublicLayout>} />
                <Route path="/answers/:slug" element={<PublicLayout><AnswerPage /></PublicLayout>} />
                <Route path="/terms" element={<PublicLayout><TermsAndConditions /></PublicLayout>} />
                <Route path="/privacy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
                <Route path="/refund-policy" element={<PublicLayout><RefundPolicy /></PublicLayout>} />
                <Route path="/shipping-policy" element={<PublicLayout><ShippingPolicy /></PublicLayout>} />

                {/* Auth Routes */}
                <Route path="/login" element={<AuthLayout><Login /></AuthLayout>} />
                <Route path="/forgot-password" element={<AuthLayout><ForgotPassword /></AuthLayout>} />

                {/* User Dashboard */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <DashboardLayout><Dashboard /></DashboardLayout>
                  </ProtectedRoute>
                } />

                {/* The Brew Circle (community) — auth required; page gates membership */}
                <Route path="/community" element={
                  <ProtectedRoute>
                    <PublicLayout><CommunityForum /></PublicLayout>
                  </ProtectedRoute>
                } />
                <Route path="/community/thread/:threadId" element={
                  <ProtectedRoute>
                    <PublicLayout><CommunityThread /></PublicLayout>
                  </ProtectedRoute>
                } />

                {/* Admin Routes */}
                <Route path="/admin/login" element={<AuthLayout><AdminLogin /></AuthLayout>} />
                <Route path="/admin" element={
                  <ProtectedRoute requiredRole="admin">
                    <AuthLayout><AdminDashboard /></AuthLayout>
                  </ProtectedRoute>
                } />

                {/* 404 */}
                <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
